/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as components from "../components/index.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Filter events by ID(s). Use bracket notation for multiple values (e.g., `id[0]=abc&id[1]=def`).
 */
export type ListEventsId = string | Array<string>;

/**
 * Filter events by tenant ID(s). Use bracket notation for multiple values (e.g., `tenant_id[0]=t1&tenant_id[1]=t2`).
 *
 * @remarks
 * When authenticated with a Tenant JWT, this parameter is ignored and the JWT's tenant is used.
 * If not provided with API key auth, returns events from all tenants.
 */
export type ListEventsTenantId = string | Array<string>;

/**
 * Filter events by matched destination ID(s). Returns events that were routed to the specified destination(s). Use bracket notation for multiple values (e.g., `destination_id[0]=d1&destination_id[1]=d2`).
 */
export type ListEventsDestinationId = string | Array<string>;

/**
 * Filter events by topic(s). Use bracket notation for multiple values (e.g., `topic[0]=user.created&topic[1]=user.updated`).
 */
export type ListEventsTopic = string | Array<string>;

/**
 * Field to sort by.
 */
export const ListEventsOrderBy = {
  Time: "time",
} as const;
/**
 * Field to sort by.
 */
export type ListEventsOrderBy = ClosedEnum<typeof ListEventsOrderBy>;

/**
 * Sort direction.
 */
export const ListEventsDir = {
  Asc: "asc",
  Desc: "desc",
} as const;
/**
 * Sort direction.
 */
export type ListEventsDir = ClosedEnum<typeof ListEventsDir>;

export type ListEventsRequest = {
  /**
   * Filter events by ID(s). Use bracket notation for multiple values (e.g., `id[0]=abc&id[1]=def`).
   */
  id?: string | Array<string> | undefined;
  /**
   * Filter events by tenant ID(s). Use bracket notation for multiple values (e.g., `tenant_id[0]=t1&tenant_id[1]=t2`).
   *
   * @remarks
   * When authenticated with a Tenant JWT, this parameter is ignored and the JWT's tenant is used.
   * If not provided with API key auth, returns events from all tenants.
   */
  tenantId?: string | Array<string> | undefined;
  /**
   * Filter events by matched destination ID(s). Returns events that were routed to the specified destination(s). Use bracket notation for multiple values (e.g., `destination_id[0]=d1&destination_id[1]=d2`).
   */
  destinationId?: string | Array<string> | undefined;
  /**
   * Filter events by topic(s). Use bracket notation for multiple values (e.g., `topic[0]=user.created&topic[1]=user.updated`).
   */
  topic?: string | Array<string> | undefined;
  /**
   * Filter events with time >= value (RFC3339 or YYYY-MM-DD format).
   */
  timeGte?: Date | undefined;
  /**
   * Filter events with time <= value (RFC3339 or YYYY-MM-DD format).
   */
  timeLte?: Date | undefined;
  /**
   * Filter events with time > value (RFC3339 or YYYY-MM-DD format).
   */
  timeGt?: Date | undefined;
  /**
   * Filter events with time < value (RFC3339 or YYYY-MM-DD format).
   */
  timeLt?: Date | undefined;
  /**
   * Number of items per page (default 100, max 1000).
   */
  limit?: number | undefined;
  /**
   * Cursor for next page of results.
   */
  next?: string | undefined;
  /**
   * Cursor for previous page of results.
   */
  prev?: string | undefined;
  /**
   * Field to sort by.
   */
  orderBy?: ListEventsOrderBy | undefined;
  /**
   * Sort direction.
   */
  dir?: ListEventsDir | undefined;
};

export type ListEventsResponse = {
  result: components.EventPaginatedResult;
};

/** @internal */
export const ListEventsId$inboundSchema: z.ZodType<
  ListEventsId,
  z.ZodTypeDef,
  unknown
> = z.union([z.string(), z.array(z.string())]);
/** @internal */
export type ListEventsId$Outbound = string | Array<string>;

/** @internal */
export const ListEventsId$outboundSchema: z.ZodType<
  ListEventsId$Outbound,
  z.ZodTypeDef,
  ListEventsId
> = z.union([z.string(), z.array(z.string())]);

export function listEventsIdToJSON(listEventsId: ListEventsId): string {
  return JSON.stringify(ListEventsId$outboundSchema.parse(listEventsId));
}
export function listEventsIdFromJSON(
  jsonString: string,
): SafeParseResult<ListEventsId, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ListEventsId$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ListEventsId' from JSON`,
  );
}

/** @internal */
export const ListEventsTenantId$inboundSchema: z.ZodType<
  ListEventsTenantId,
  z.ZodTypeDef,
  unknown
> = z.union([z.string(), z.array(z.string())]);
/** @internal */
export type ListEventsTenantId$Outbound = string | Array<string>;

/** @internal */
export const ListEventsTenantId$outboundSchema: z.ZodType<
  ListEventsTenantId$Outbound,
  z.ZodTypeDef,
  ListEventsTenantId
> = z.union([z.string(), z.array(z.string())]);

export function listEventsTenantIdToJSON(
  listEventsTenantId: ListEventsTenantId,
): string {
  return JSON.stringify(
    ListEventsTenantId$outboundSchema.parse(listEventsTenantId),
  );
}
export function listEventsTenantIdFromJSON(
  jsonString: string,
): SafeParseResult<ListEventsTenantId, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ListEventsTenantId$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ListEventsTenantId' from JSON`,
  );
}

/** @internal */
export const ListEventsDestinationId$inboundSchema: z.ZodType<
  ListEventsDestinationId,
  z.ZodTypeDef,
  unknown
> = z.union([z.string(), z.array(z.string())]);
/** @internal */
export type ListEventsDestinationId$Outbound = string | Array<string>;

/** @internal */
export const ListEventsDestinationId$outboundSchema: z.ZodType<
  ListEventsDestinationId$Outbound,
  z.ZodTypeDef,
  ListEventsDestinationId
> = z.union([z.string(), z.array(z.string())]);

export function listEventsDestinationIdToJSON(
  listEventsDestinationId: ListEventsDestinationId,
): string {
  return JSON.stringify(
    ListEventsDestinationId$outboundSchema.parse(listEventsDestinationId),
  );
}
export function listEventsDestinationIdFromJSON(
  jsonString: string,
): SafeParseResult<ListEventsDestinationId, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ListEventsDestinationId$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ListEventsDestinationId' from JSON`,
  );
}

/** @internal */
export const ListEventsTopic$inboundSchema: z.ZodType<
  ListEventsTopic,
  z.ZodTypeDef,
  unknown
> = z.union([z.string(), z.array(z.string())]);
/** @internal */
export type ListEventsTopic$Outbound = string | Array<string>;

/** @internal */
export const ListEventsTopic$outboundSchema: z.ZodType<
  ListEventsTopic$Outbound,
  z.ZodTypeDef,
  ListEventsTopic
> = z.union([z.string(), z.array(z.string())]);

export function listEventsTopicToJSON(
  listEventsTopic: ListEventsTopic,
): string {
  return JSON.stringify(ListEventsTopic$outboundSchema.parse(listEventsTopic));
}
export function listEventsTopicFromJSON(
  jsonString: string,
): SafeParseResult<ListEventsTopic, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ListEventsTopic$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ListEventsTopic' from JSON`,
  );
}

/** @internal */
export const ListEventsOrderBy$inboundSchema: z.ZodNativeEnum<
  typeof ListEventsOrderBy
> = z.nativeEnum(ListEventsOrderBy);
/** @internal */
export const ListEventsOrderBy$outboundSchema: z.ZodNativeEnum<
  typeof ListEventsOrderBy
> = ListEventsOrderBy$inboundSchema;

/** @internal */
export const ListEventsDir$inboundSchema: z.ZodNativeEnum<
  typeof ListEventsDir
> = z.nativeEnum(ListEventsDir);
/** @internal */
export const ListEventsDir$outboundSchema: z.ZodNativeEnum<
  typeof ListEventsDir
> = ListEventsDir$inboundSchema;

/** @internal */
export const ListEventsRequest$inboundSchema: z.ZodType<
  ListEventsRequest,
  z.ZodTypeDef,
  unknown
> = z.object({
  id: z.union([z.string(), z.array(z.string())]).optional(),
  tenant_id: z.union([z.string(), z.array(z.string())]).optional(),
  destination_id: z.union([z.string(), z.array(z.string())]).optional(),
  topic: z.union([z.string(), z.array(z.string())]).optional(),
  "time[gte]": z.string().datetime({ offset: true }).transform(v => new Date(v))
    .optional(),
  "time[lte]": z.string().datetime({ offset: true }).transform(v => new Date(v))
    .optional(),
  "time[gt]": z.string().datetime({ offset: true }).transform(v => new Date(v))
    .optional(),
  "time[lt]": z.string().datetime({ offset: true }).transform(v => new Date(v))
    .optional(),
  limit: z.number().int().default(100),
  next: z.string().optional(),
  prev: z.string().optional(),
  order_by: ListEventsOrderBy$inboundSchema.default("time"),
  dir: ListEventsDir$inboundSchema.default("desc"),
}).transform((v) => {
  return remap$(v, {
    "tenant_id": "tenantId",
    "destination_id": "destinationId",
    "time[gte]": "timeGte",
    "time[lte]": "timeLte",
    "time[gt]": "timeGt",
    "time[lt]": "timeLt",
    "order_by": "orderBy",
  });
});
/** @internal */
export type ListEventsRequest$Outbound = {
  id?: string | Array<string> | undefined;
  tenant_id?: string | Array<string> | undefined;
  destination_id?: string | Array<string> | undefined;
  topic?: string | Array<string> | undefined;
  "time[gte]"?: string | undefined;
  "time[lte]"?: string | undefined;
  "time[gt]"?: string | undefined;
  "time[lt]"?: string | undefined;
  limit: number;
  next?: string | undefined;
  prev?: string | undefined;
  order_by: string;
  dir: string;
};

/** @internal */
export const ListEventsRequest$outboundSchema: z.ZodType<
  ListEventsRequest$Outbound,
  z.ZodTypeDef,
  ListEventsRequest
> = z.object({
  id: z.union([z.string(), z.array(z.string())]).optional(),
  tenantId: z.union([z.string(), z.array(z.string())]).optional(),
  destinationId: z.union([z.string(), z.array(z.string())]).optional(),
  topic: z.union([z.string(), z.array(z.string())]).optional(),
  timeGte: z.date().transform(v => v.toISOString()).optional(),
  timeLte: z.date().transform(v => v.toISOString()).optional(),
  timeGt: z.date().transform(v => v.toISOString()).optional(),
  timeLt: z.date().transform(v => v.toISOString()).optional(),
  limit: z.number().int().default(100),
  next: z.string().optional(),
  prev: z.string().optional(),
  orderBy: ListEventsOrderBy$outboundSchema.default("time"),
  dir: ListEventsDir$outboundSchema.default("desc"),
}).transform((v) => {
  return remap$(v, {
    tenantId: "tenant_id",
    destinationId: "destination_id",
    timeGte: "time[gte]",
    timeLte: "time[lte]",
    timeGt: "time[gt]",
    timeLt: "time[lt]",
    orderBy: "order_by",
  });
});

export function listEventsRequestToJSON(
  listEventsRequest: ListEventsRequest,
): string {
  return JSON.stringify(
    ListEventsRequest$outboundSchema.parse(listEventsRequest),
  );
}
export function listEventsRequestFromJSON(
  jsonString: string,
): SafeParseResult<ListEventsRequest, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ListEventsRequest$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ListEventsRequest' from JSON`,
  );
}

/** @internal */
export const ListEventsResponse$inboundSchema: z.ZodType<
  ListEventsResponse,
  z.ZodTypeDef,
  unknown
> = z.object({
  Result: components.EventPaginatedResult$inboundSchema,
}).transform((v) => {
  return remap$(v, {
    "Result": "result",
  });
});
/** @internal */
export type ListEventsResponse$Outbound = {
  Result: components.EventPaginatedResult$Outbound;
};

/** @internal */
export const ListEventsResponse$outboundSchema: z.ZodType<
  ListEventsResponse$Outbound,
  z.ZodTypeDef,
  ListEventsResponse
> = z.object({
  result: components.EventPaginatedResult$outboundSchema,
}).transform((v) => {
  return remap$(v, {
    result: "Result",
  });
});

export function listEventsResponseToJSON(
  listEventsResponse: ListEventsResponse,
): string {
  return JSON.stringify(
    ListEventsResponse$outboundSchema.parse(listEventsResponse),
  );
}
export function listEventsResponseFromJSON(
  jsonString: string,
): SafeParseResult<ListEventsResponse, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ListEventsResponse$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ListEventsResponse' from JSON`,
  );
}
